/** 抽奖组件, 任务类活动管理，消费转盘 */
import { onMounted, watch, reactive, computed, ref, nextTick, toRefs } from 'vue'
import LGSdk from '@/packages/plugins/languageSDK/index'
import useAppStore from '@/h5/stores/app'
import Toast from '@/h5/components/toast'
import ConfirmDialog from '@/h5/components/confirmDialog'
import { delay } from '@/packages/plugins/utils'
import { formatDateByLanguage } from '@/packages/plugins/dateUtils'
import { cloneDeep } from 'lodash'
import { computeRatioPx } from '@/packages/plugins/utils'

import { customRequest, baseSecret } from '@/h5/api/activity'
import { randomNum, getTimestamp, getSignedData } from '@/packages/plugins/utils'
import AppReq from '@/h5/utils/appActions/index'
import UnityBridge from '@/h5/utils/appActions/unityBridge'

/** 转盘开放事件 */
export const LUCKY_EVENTS = [
  {
    name: 'startRoulette', // 事件名
    label: '开始抽奖', // 事件描述
    params: [
      {
        label: '消耗项下标', // 表单名称
        value: 'index' // 表单model字段名称
      }
    ] // 事件参数
  },
  {
    name: 'againRoulette', // 事件名
    label: '再次抽奖' // 事件描述
  },
  {
    name: 'closeDialog', // 事件名
    label: '关闭中奖弹窗' // 事件描述
  },
  {
    name: 'refreshMoneyBalances', // 事件名
    label: '刷新抽奖消耗余额' // 事件描述
  }
]

/** 转盘开放数据源 */
export const LUCKY_DATA_SOURCE = [
  {
    label: '货币余额',
    keys: 'moneyBalances[]',
    description: '钻石余额，数组索引固定为0'
  },
  {
    label: '用户累计抽奖次数',
    keys: 'userRoulette.count'
  },
  {
    label: '抽奖记录列表',
    keys: 'raffleRecord'
  },
  {
    label: '抽奖记录-时间',
    keys: 'raffleRecord[].time'
  },
  {
    label: '抽奖记录-奖励列表',
    keys: 'raffleRecord[].rewards'
  },
  {
    label: '抽奖记录-奖励列表-图片',
    keys: 'raffleRecord[].rewards[].src'
  },
  {
    label: '抽奖记录-奖励列表-数量',
    keys: 'raffleRecord[].rewards[].count'
  },
  {
    label: '中奖列表',
    keys: 'rewards'
  },
  {
    label: '中奖列表-奖品图片',
    keys: 'rewards[].src'
  },
  {
    label: '中奖列表-奖品数量',
    keys: 'rewards[].count'
  },
  {
    label: '上次抽奖按钮项',
    keys: 'rouletteBtnIndex'
  },
  {
    label: '抽奖档位消耗数量',
    keys: 'rouletteCfg.ways[].cost.count',
    description: '各抽奖档位 cost.count，下标与抽奖按钮一致'
  }
]

/** 转盘接口 */
export const API = {
  /**
   * 获取活动配置
   * @param {*} activityId
   * @returns
   */
  getActivityConfig: (activityId) => {
    const params = {
      id: activityId,
      sign: '',
      appId: 1004,
      timestamp: getTimestamp(),
      random: randomNum(6),
      activityType: 3
    }
    params.sign = getSignedData(params, baseSecret)
    return customRequest(null, '/v1/activityManage/configList', 'get', { 'Content-Type': 'application/json; charset=utf-8' }, params)
  },
  /**
   * 抽奖
   * @param {*} activityId
   * @param {*} wayId
   * @returns
   */
  rouletteReq: (activityId, wayId) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi('5', { activityId, wayId })
    } else {
      return null
    }
  },
  /**
   * 历史记录
   * @param {*} activityId
   * @returns
   */
  rouletteRecordReq: (activityId) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi('6', { activityId })
    } else {
      return null
    }
  },
  /**
   * 历史记录
   * @param {*} activityId
   * @returns
   */
  rouletteGetInfoH5Req: (activityId) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi('9', { activityId })
    } else {
      return null
    }
  },
  /**
   * 获取用户代币余额
   * @param {*} ids
   * @returns
   */
  getUserCurrencys: (ids) => {
    if (AppReq.requestUnityApi) {
      return AppReq.requestUnityApi('1', ids)
    } else {
      return null
    }
  },
  /**
   * 获取用户常量
   * @returns
   */
  getUserConstant: () => {
    return new Promise((resolve) => {
      UnityBridge.callHandler('getUserConstant', (_, data) => {
        let constant = {
          diamond: 0,
          sex: 0,
          coin: 0,
          surpriseCoin: 0
        }
        if (_ === false) {
          return resolve(constant)
        }
        try {
          const params = JSON.parse(data)
          constant = Object.assign(constant, params)
          return resolve(constant)
        } catch (e) {
          console.error(e)
          return resolve(constant)
        }
      })
    })
  }
}

/**
 * 转盘通用hook
 * @returns
 */
export const useLucky = (props, wheelCfg, initWheelFunc, setPrizesFunc) => {
  // 转盘配置
  const componentRef = ref(null)
  const luckyWheelRef = ref(null)
  const upKey = ref(0)
  const appStore = useAppStore ? useAppStore() : null

  // 组件属性
  const state = reactive({
    // 语言
    language: 'en',
    // 组件uuid
    uuid: '',
    // 活动配置
    rouletteCfg: {},
    // 抽奖档位选中下标
    rouletteBtnIndex: 0,
    // 抽奖结果
    rouletteResult: {},
    // 保存抽中的奖项id
    saveAwardIds: [],
    // 抽奖间隔动画
    rouletterAnimationTime: 2000,
    // 是否抽奖中
    isRouletter: false,
    // 中奖icon
    rewardsSrcList: [],
    // 挂载数据
    moneyBalancesInfo: {},
    dataSource: { moneyBalances: [], raffleRecord: [], rewards: [], rouletteBtnIndex: 0, userRoulette: {}, rouletteCfg: { ways: [] } },
    checkLabel: LGSdk.t('luckyWheel.noAnimation') || '不显示动画',
    checkSrc: props.checkBoxSrc || '',
    awardsAry: [] // 合并抽奖项后的奖项数组信息
  })
  const { checkLabel, checkSrc } = toRefs(state)

  // 更新多语言内容
  LGSdk.subscribeToLocale(() => {
    state.checkLabel = LGSdk.t('luckyWheel.noAnimation') || '不显示动画'
  })

  /**
   * 抽奖结束回调
   */
  const endCallback = () => {
    startrouletteQueue()
  }

  /** 设置勾选栏样式 */
  const showCustomCheck = computed(() => {
    return props.checkBoxSrc && props.checkBoxSelectSrc
  })
  const checkboxImgStyle = computed(() => {
    const checkBoxSize = computeRatioPx(28)
    const marginRight10 = computeRatioPx(10)
    return {
      width: checkBoxSize,
      height: checkBoxSize,
      marginRight: marginRight10
    }
  })
  const setCheckStyle = () => {
    const style = document.createElement('style')
    const marginRight45 = computeRatioPx(85)
    const marginTop40 = computeRatioPx(40)
    const checkBoxSize = computeRatioPx(28)
    const fontSize16 = computeRatioPx(32)
    const marginRight10 = computeRatioPx(10)
    const borderRadius3 = computeRatioPx(6)
    style.innerHTML = `
      .xy-lucky-wheel{
        position: relative;
      }
      .xy-lucky-wheel .check-box {
        padding-right: ${marginRight45};
        margin-bottom: ${marginTop40};
      }
      .xy-lucky-wheel .check-box .check-label {
        color: ${props.checkBoxColor};
      }
      .xy-lucky-wheel .check-box #myCheckbox + label::before {
        width: ${checkBoxSize};
        height: ${checkBoxSize};
        margin-right: ${marginRight10};
        border-radius: ${borderRadius3};
        border: 1px solid ${props.checkBoxColor};
      }
      .xy-lucky-wheel .check-box #myCheckbox:checked + label::before {
        color: ${props.checkBoxColor};
        font-size: ${fontSize16};
        line-height: ${checkBoxSize};
      }
    `
    document.head.appendChild(style)
  }
  const checkBoxStyle = computed(() => {
    return {
      marginTop: computeRatioPx(props.checkBoxTop)
    }
  })

  /** 保存抽奖动画设置 */
  const saveRouletteAnimation = (flag) => {
    const uid = appStore?.uid || 0
    localStorage.setItem(`rouletteAnimation${props.activityId}-${uid}`, flag ? new Date().getTime() : 0)
  }
  const getRouletterAnimation = () => {
    const uid = appStore?.uid || 0
    const time = localStorage.getItem(`rouletteAnimation${props.activityId}-${uid}`) || 0
    return new Date().getTime() - Number(time) > 24 * 60 * 60 * 1000
  }
  const handleCheckChange = (e) => {
    // console.log('handleCheckChange', e.target.checked)
    if (showCustomCheck.value) {
      saveRouletteAnimation(getRouletterAnimation())
      setCheckStates()
    } else {
      saveRouletteAnimation(e.target.checked)
    }
  }
  const setCheckStates = () => {
    nextTick(() => {
      if (showCustomCheck.value) {
        state.checkSrc = getRouletterAnimation() ? props.checkBoxSrc : props.checkBoxSelectSrc
      } else {
        const checkbox = document.getElementById('myCheckbox')
        if (checkbox) {
          checkbox.checked = !getRouletterAnimation()
        }
      }
    })
  }

  /** 是否跳过钻石消耗弹框 */
  const saveConfirmStates = (flag) => {
    const uid = appStore?.uid || 0
    localStorage.setItem(`rouletteConfirmStates${props.activityId}-${uid}`, flag ? new Date().getTime() : 0)
  }
  const getConfirmStates = () => {
    const uid = appStore?.uid || 0
    const time = localStorage.getItem(`rouletteConfirmStates${props.activityId}-${uid}`) || 0
    return new Date().getTime() - Number(time) > 24 * 60 * 60 * 1000
  }

  const getRouletteAwardIndex = (id) => {
    const index = state.awardsAry.findIndex((item) => item.id.indexOf(id) > -1)
    // console.log('---state.rouletteCfg.rewards---', wheelCfg.value.prizes, id, index)
    return index
  }

  /** 点击抽奖按钮 */
  const startRoulette = async (params) => {
    const index = Number(params.index)
    if (state.isRouletter) {
      return
    }
    // 保存抽奖项
    state.rouletteBtnIndex = index
    state.dataSource.rouletteBtnIndex = index
    appStore.setPageDataSource(state.uuid, state.dataSource || null)

    const waysCfg = state.rouletteCfg.ways[index]
    console.log('点击抽奖按钮', index, waysCfg)
    if (waysCfg.cost.type === 0 && waysCfg.cost.contentId === '2' && getConfirmStates()) {
      // 为钻石抽奖,判断用户是否勾选不提示弹框
      ConfirmDialog &&
        (
          await ConfirmDialog({
            title: LGSdk.t('public.tips'),
            message: LGSdk.t('luckyWheel.buyConfirmInfo', { count: waysCfg.cost.count }),
            confirmText: LGSdk.t('public.confirm'),
            cancelText: LGSdk.t('public.cancel'),
            checkedText: LGSdk.t('luckyWheel.checked')
          }).then(async ({ type, data }) => {
            // console.log('checked', type, data)
            if (type === 'confirm') {
              // 确定
              saveConfirmStates(data)
              if (props.noView) {
                await rouletteStartByOnView().catch((e) => {
                  getRouletteGetInfo()
                  throw e
                })
              } else {
                await rouletteStart().catch((e) => {
                  getRouletteGetInfo()
                  throw e
                })
              }
            } else {
              saveConfirmStates(false)
            }
          })
        ).catch((e) => {
          throw e
        })
    } else {
      if (props.noView) {
        await rouletteStartByOnView().catch((e) => {
          getRouletteGetInfo()
          throw e
        })
      } else {
        await rouletteStart().catch((e) => {
          getRouletteGetInfo()
          throw e
        })
      }
    }
  }
  const startrouletteQueue = async () => {
    // 启动抽奖动画队列
    await delay(500)
    state.rouletteResult.awardId && state.rouletteResult.awardId.shift()
    if (state.rouletteResult.awardId?.length) {
      // 继续动画
      setTimeout(() => {
        stop(getRouletteAwardIndex(state.rouletteResult.awardId[0]))
      }, props.accelerationTime + 500)
      start()
    } else {
      showRewardsDialog()
    }
  }

  const showRewardsDialog = () => {
    // 显示奖励弹框
    state.rewardsSrcList = []
    const rewardsMap = {}
    if (!state.rouletteResult.rewards) {
      return
    }
    state.rouletteResult.rewards.forEach((item) => {
      const key = `${item.type},${item.contentId}`
      if (props.prizeImages[key]) {
        const src = props.prizeImages[key].find((item) => item.language === state.language)?.src || ''
        rewardsMap[key] = { src, count: item.count }
      }
    })
    state.saveAwardIds.forEach((item) => {
      const index = getRouletteAwardIndex(item)
      // console.log('wheelCfg.value.prizes', wheelCfg.value.prizes, index, item)
      const rewards = state.awardsAry[index].rewards
      // 获取转盘项奖励信息，根据奖项配置拆分奖项配置
      let hasReward = false
      rewards.forEach((itemR) => {
        const key = `${itemR.type},${itemR.contentId}`
        if (rewardsMap[key] && rewardsMap[key].count > 0) {
          hasReward = true
          rewardsMap[key].count = rewardsMap[key].count - itemR.count
          if (rewardsMap[key].src) {
            // 没有上传图片的奖品，不展示
            state.rewardsSrcList.push({ src: rewardsMap[key].src, count: itemR.count })
          }
        }
      })
      if (!hasReward && state.rouletteCfg?.replacements?.length) {
        // 查询奖励替换物品
        for (let i = 0; i < state.rouletteCfg.replacements.length; i++) {
          const replaceInfo = state.rouletteCfg.replacements[i]
          let has = false
          if (replaceInfo?.toRewards?.length) {
            for (let j = 0; j < replaceInfo.toRewards.length; j++) {
              const itemR = replaceInfo.toRewards[j]
              const key = `${itemR.type},${itemR.contentId}`
              if (rewardsMap[key] && rewardsMap[key].count > 0) {
                rewardsMap[key].count = rewardsMap[key].count - itemR.count
                if (rewardsMap[key].src) {
                  // 没有上传图片的奖品，不展示
                  state.rewardsSrcList.push({ src: rewardsMap[key].src, count: itemR.count })
                }
                has = true
                break
              }
            }
          }
          if (has) {
            // console.log('核销替换物成功')
            break
          }
        }
      }
    })
    // 判断是否有多个替换物未拆解奖品
    Object.keys(rewardsMap).forEach((key) => {
      if (rewardsMap[key].count > 0) {
        if (rewardsMap[key].src) {
          // 没有上传图片的奖品，不展示
          state.rewardsSrcList.push({ src: rewardsMap[key].src, count: rewardsMap[key].count })
        }
      }
    })
    state.dataSource.rewards = state.rewardsSrcList
    console.log('----抽中结果', state.rewardsSrcList)
    appStore.setPageDataSource(state.uuid, state.dataSource || null)

    appStore.setNodeAttribute(props.dialogUUID, 'hiden', false)
    // state.showRewards = true
    // 获取用户抽奖信息
    getRouletteGetInfo()
  }

  /**
   * 无视图抽奖
   * @returns
   */
  const rouletteStartByOnView = async () => {
    // 判断代币钻石是否充足
    const cost = state.rouletteCfg.ways[state.rouletteBtnIndex].cost
    const hasMoney = state.moneyBalancesInfo[`${cost.type}${cost.contentId}`] - cost.count
    console.log('抽奖消耗', hasMoney, state.moneyBalancesInfo)
    if (hasMoney < 0) {
      Toast && Toast({ message: LGSdk.t('luckyWheel.2009005'), duration: 1500 })
      throw new Error('luckyWheel.2009005')
    }
    state.isRouletter = true
    try {
      const res = await API.rouletteReq(props.activityId, state.rouletteBtnIndex)
      if (res.data.status === 1) {
        // 抽奖成功
        state.rouletteResult = res.data || {}
        state.saveAwardIds = cloneDeep(res.data.awardId)
        // 唯一奖励重复, 没有真实发货奖品
        const hasReward = state.rouletteResult.rewards.length > 0
        // 判断是否执行抽奖动画
        if (hasReward) {
          // 跳过动画
          showRewardsDialog()
        }
      } else {
        // 多语言提示
        const statusCodes = [2009001, 2009005]
        let msg = 'error: ' + res.data.status
        if (statusCodes.includes(Number(res.data.status))) {
          msg = LGSdk.t(`luckyWheel.${res.data.status}`)
        }
        Toast && Toast({ message: msg, duration: 1500 })
        state.isRouletter = false
      }
      getMoneyBalances()
      if (res.data.status === 2009005) {
        throw new Error('luckyWheel.2009005')
      }
    } catch (error) {
      if (error.message === 'luckyWheel.2009005') {
        throw error
      } else {
        console.log('handleStart-error', error)
        state.isRouletter = false
      }
    }
  }

  const rouletteStart = async () => {
    // 判断代币钻石是否充足
    const cost = state.rouletteCfg.ways[state.rouletteBtnIndex].cost
    const hasMoney = state.moneyBalancesInfo[`${cost.type}${cost.contentId}`] - cost.count
    console.log('抽奖消耗', hasMoney, state.moneyBalancesInfo)
    if (hasMoney < 0) {
      Toast && Toast({ message: LGSdk.t('luckyWheel.2009005'), duration: 1500 })
      throw new Error('luckyWheel.2009005')
    }
    state.isRouletter = true
    try {
      const res = await API.rouletteReq(props.activityId, state.rouletteBtnIndex)
      if (res.data.status === 1) {
        // 抽奖成功
        state.rouletteResult = res.data || {}
        state.saveAwardIds = cloneDeep(res.data.awardId)
        // 唯一奖励重复, 没有真实发货奖品
        const hasReward = state.rouletteResult.rewards.length > 0
        // 判断是否执行抽奖动画
        if (!getRouletterAnimation() && hasReward) {
          // 跳过动画
          showRewardsDialog()
        } else {
          start()
          stop(getRouletteAwardIndex(state.rouletteResult.awardId[0]))
        }
      } else {
        // 多语言提示
        const statusCodes = [2009001, 2009005]
        let msg = 'error: ' + res.data.status
        if (statusCodes.includes(Number(res.data.status))) {
          msg = LGSdk.t(`luckyWheel.${res.data.status}`)
        }
        Toast && Toast({ message: msg, duration: 1500 })
        state.isRouletter = false
        stop()
      }
      getMoneyBalances()
      if (res.data.status === 2009005) {
        throw new Error('luckyWheel.2009005')
      }
    } catch (error) {
      if (error.message === 'luckyWheel.2009005') {
        throw error
      } else {
        console.log('handleStart-error', error)
        state.isRouletter = false
        stop()
      }
    }
  }

  /**
   * 开始抽奖
   */
  const start = () => {
    console.time('start-time')
    luckyWheelRef.value && luckyWheelRef.value.play()
  }
  /**
   * 停止抽奖
   * @param {*} index 停止下标
   * @returns
   */
  const stop = (index = 0) => {
    luckyWheelRef.value && luckyWheelRef.value.stop(index)
    return delay(props.decelerationTime + 500)
  }

  /** 将 rouletteCfg.ways 同步到页面数据源 */
  const syncRouletteCfgDataSource = () => {
    const ways = state.rouletteCfg?.ways || []
    state.dataSource.rouletteCfg = {
      ways: ways.map((item) => ({
        cost: {
          count: item?.cost?.count ?? 0
        }
      }))
    }
    if (state.uuid) {
      appStore && appStore.setPageDataSource(state.uuid, state.dataSource || null)
    }
  }

  /**
   * 获取转盘配置
   * @returns
   */
  const getConfig = async () => {
    if (props.activityId) {
      const res = await API.getActivityConfig(props.activityId)
      state.rouletteCfg = res?.data?.list[0]?.rouletteCfg || {}
      syncRouletteCfgDataSource()
      initWheel()
      return res.data || null
    }
  }

  const closeDialog = () => {
    // state.showRewards = false
    appStore.setNodeAttribute(props.dialogUUID, 'hiden', true)
    state.isRouletter = false
    // 获取用户代币,钻石
    // getMoneyBalances()
    getHistoreList()
  }

  const refreshMoneyBalances = () => {
    getMoneyBalances()
  }

  const againRoulette = async () => {
    // state.showRewards = false
    appStore.setNodeAttribute(props.dialogUUID, 'hiden', true)
    state.isRouletter = false
    // 再次抽奖
    await startRoulette({ index: state.rouletteBtnIndex }).catch((e) => {
      throw e
    })
    // getMoneyBalances()
  }

  const getRouletteGetInfo = async () => {
    // 获取转盘用户抽奖信息
    const res = await API.rouletteGetInfoH5Req(props.activityId)
    state.dataSource.userRoulette = res.data
    appStore.setPageDataSource(state.uuid, state.dataSource || null)
    console.log('getRouletteGetInfo', res, state.dataSource.userRoulette)
  }

  const getHistoreList = async () => {
    const res = await API.rouletteRecordReq(props.activityId)
    const list = res.data.records?.length ? res.data.records : []
    const historeList = list.map((item) => {
      const rewards = []
      if (item.rewards?.length) {
        item.rewards.forEach((itemR) => {
          const key = `${itemR.type},${itemR.contentId}`
          if (props.prizeImages[key]) {
            const src = props.prizeImages[key].find((itemP) => itemP.language === state.language)?.src || ''
            if (src) {
              // 有奖励图片就展示
              rewards.push({ src, count: itemR.count })
            }
          }
        })
      }
      return {
        time: formatDateByLanguage(item.getTime, state.language, 'YYYY/MM/DD  HH:mm', 'YYYY/MM/DD  HH:mm'),
        rewards
      }
    })
    // 倒序
    historeList.reverse()
    state.dataSource.raffleRecord = historeList
    appStore.setPageDataSource(state.uuid, state.dataSource || null)
    // console.log('getHistoreList', res, state.dataSource.raffleRecord)
  }

  const getMoneyBalances = async () => {
    // 获取用户代币,钻石
    let hasDiamond = false
    let ids = {}
    const moneyAry = []
    const moneyMap = {}
    state.rouletteCfg?.ways?.forEach((item) => {
      if (item.cost.type === 0 && item.cost.contentId === '2') {
        // 钻石
        hasDiamond = true
        moneyMap['02'] = 0
      } else if (item.cost.type === 8) {
        // 代币
        ids[item.cost.contentId] = true
      }
    })
    // console.log('getMoneyBalances', state.rouletteCfg.ways)
    // 查询钻石
    if (hasDiamond) {
      const res = await API.getUserConstant()
      moneyAry.push(res.diamond)
      moneyMap['02'] = Number(res.diamond)
    }
    // 查询代币
    if (Object.keys(ids).length) {
      const resC = await API.getUserCurrencys({ ids: Object.keys(ids) })
      if (resC?.data?.list) {
        resC?.data?.list.forEach((item) => {
          moneyAry.push(item.count)
          moneyMap['8' + item.id] = item.count
        })
      }
    }
    state.dataSource.moneyBalances = moneyAry
    state.moneyBalancesInfo = moneyMap
    appStore.setPageDataSource(state.uuid, state.dataSource || null)
    console.log('转盘代币余额', moneyAry, state.moneyBalancesInfo)
  }

  /**
   * 转盘初始化
   */
  const initWheel = () => {
    // 无视图无需初始化UI配置
    setAwardsInfo()
    if (props.noView) {
      return
    }
    initWheelFunc && initWheelFunc()
    // 设置奖项
    setPrizes()
    // 设置勾选栏样式
    setCheckStyle()
    setCheckStates()
    upKey.value++
    // console.log('initWheel', wheelCfg.value)
  }

  const setAwardsInfo = () => {
    // 设置奖项信息
    let sortedAndMergedAwards = []
    if (state.rouletteCfg?.awards?.length) {
      sortedAndMergedAwards = state.rouletteCfg.awards
        .sort((a, b) => a.locationId - b.locationId)
        .reduce((acc, current) => {
          const existing = acc.find((item) => item.locationId === current.locationId)
          if (existing) {
            // 奖项id合并
            existing.id += `,${current.id}`
            // 奖项奖励合并
            existing.rewards = existing.rewards.concat(current.rewards)
          } else {
            current.id = `${current.id}`
            acc.push({ ...current })
          }
          return acc
        }, [])
    }
    state.awardsAry = sortedAndMergedAwards
  }

  const setPrizes = () => {
    if (props.prizes.length === 0) {
      return
    }
    // 设置奖项
    setPrizesFunc && setPrizesFunc(state.awardsAry, state.language)
  }

  /**
   * 初始化
   */
  const init = async () => {
    const res = await API.getActivityConfig(props.activityId)
    state.rouletteCfg = res?.data?.list[0]?.rouletteCfg || {}
    syncRouletteCfgDataSource()
    if (res?.data?.list[0]) {
      initWheel()
      getMoneyBalances()
      getHistoreList()
      getRouletteGetInfo()
    }
  }

  /***
   * 更新转盘
   */
  watch(
    () => props,
    () => {
      initWheel()
    },
    { immediate: true, deep: true }
  )

  onMounted(() => {
    if (componentRef.value) {
      state.language = componentRef.value.getAttribute('language') || 'en'
      state.uuid = componentRef.value.getAttribute('id') || ''
    }

    if (import.meta.env.VITE_APP_TAG === 'H5') {
      init()
    } else {
      getConfig()
    }
  })

  return {
    componentRef,
    luckyWheelRef,
    upKey,
    checkLabel,
    checkSrc,
    endCallback,
    showCustomCheck,
    checkboxImgStyle,
    setCheckStyle,
    checkBoxStyle,
    handleCheckChange,
    start,
    stop,
    getConfig,
    startRoulette,
    againRoulette,
    closeDialog,
    refreshMoneyBalances
  }
}
